<template>
  <div>
    <div v-if="showOnTop" class="multi-files-badges">
      <file-badge
        v-for="(file, id) in effectiveFiles"
        :id="id.toString()"
        :key="id"
        :progress="file.progress"
        :name="file.fileName"
        :loading="file.loading"
        :file-size="file.fileSize"
        :wrapper-class="wrapperClass(file)"
        :on-remove="handleFileRemoved"
      />
    </div>
    <direct-upload-dropzone
      :on-file-added="handleFileAdded"
      :on-file-progress="handleFileProgress"
      :on-file-uploaded="handleFileUploaded"
      :accept="accept"
      :multiple="true"
    >
      <slot />
    </direct-upload-dropzone>
    <div v-if="showBelow" class="multi-files-badges">
      <file-badge
        v-for="(file, id) in effectiveFiles"
        :id="id.toString()"
        :key="id"
        :progress="file.progress"
        :name="file.fileName"
        :loading="file.loading"
        :file-size="file.fileSize"
        :on-remove="handleFileRemoved"
      />
    </div>
  </div>
</template>

<script lang="ts">
export default {
  name: 'MultiFileDirectUploader',
}
</script>
<script setup lang="ts">
import { BadgePosition, FileWithProgress } from './types'
import { Blob as ActiveBlob } from '@rails/activestorage'

import find from 'lodash/find'
import { computed, ref } from 'vue'

import fileBadge from './file-badge.vue'
import directUploadDropzone from './direct-upload-dropzone.vue'
import { DO_NOT_SHOW, SHOW_ON_TOP, SHOW_BELOW } from './constants'

interface Props {
  accept?: string
  fileBadgePosition?: BadgePosition
  existingFiles?: Record<string, FileWithProgress>
  onAllFilesUploaded?: (status: boolean) => void
  onFileRemoved?: (uuid: string, length: number) => void
  onFileUploaded?: (
    file: FileWithProgress,
    uuid: string,
    length: number
  ) => void
  onFileUploadProgress?: (
    uuid: string,
    file: FileWithProgress,
    progress: string
  ) => void
}

const props = withDefaults(defineProps<Props>(), {
  accept: '',
  fileBadgePosition: DO_NOT_SHOW,
  existingFiles: () => ({}),
  onAllFilesUploaded: () => {},
  onFileRemoved: () => {},
  onFileUploaded: () => {},
  onFileUploadProgress: () => {},
})

const files = ref<Record<string, FileWithProgress>>({ ...props.existingFiles })

const showOnTop = computed(() => props.fileBadgePosition === SHOW_ON_TOP)
const showBelow = computed(() => props.fileBadgePosition === SHOW_BELOW)

const effectiveFiles = computed(() => {
  const fileKeys = Object.keys(files.value)

  return fileKeys.reduce((acc, f) => {
    acc = {
      ...acc,
      [f]: {
        ...files.value[f],
        wrapperClass: wrapperClass(files.value[f]),
        fileName: files.value[f]?.file?.name || '',
        fileSize: files.value[f]?.file?.size || 0,
      },
    }

    return acc
  }, {} as Record<string, FileWithProgress & { wrapperClass: string; fileName: string; fileSize: number }>)
})

const allFilesUploadedCheck = () => {
  const allFilesUploaded = !find(files.value, function (f) {
    return !f.blob
  })

  props.onAllFilesUploaded(allFilesUploaded)
}

const handleFileUploaded = ({
  blob,
  uuid,
}: {
  blob: ActiveBlob
  uuid: string
}) => {
  const file = files.value[uuid]

  if (file) {
    files.value[uuid] = {
      ...file,
      blob,
      loading: false,
    }

    props.onFileUploaded(
      files.value[uuid],
      uuid,
      Object.keys(files.value).length
    )
  }

  allFilesUploadedCheck()
}

const handleFileAdded = ({ file, uuid }: { file: File; uuid: string }) => {
  const newFile = {
    blob: null,
    loading: false,
    file,
    progress: 0,
    existing: false,
  }

  files.value = {
    ...files.value,
    [uuid]: { ...newFile },
  }

  allFilesUploadedCheck()
}

const handleFileProgress = ({
  uuid,
  progress,
}: {
  uuid: string
  progress: string
}) => {
  const file = files.value[uuid]

  if (file) {
    files.value[uuid] = {
      ...files.value[uuid],
      progress,
      loading: true,
    }

    props.onFileUploadProgress(uuid, files.value[uuid], progress)
  }
}

const handleFileRemoved = (uuid: string) => {
  const file = files.value[uuid]

  if (file) {
    const { [uuid]: toDelete, ...rest } = files.value

    files.value = { ...rest }

    props.onFileRemoved(uuid, Object.keys(files.value).length)
  }

  allFilesUploadedCheck()
}

const wrapperClass = (file: FileWithProgress) => {
  return file.existing ? 'sf-inline' : 'sf-full-width'
}
</script>
